import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    ToggleButton,
    Pagination,
    Skeleton
} from '@mui/material';
import {  FaEye, FaEdit } from 'react-icons/fa';
import { FaWarehouse } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import { getData, postData, deleteData, putData } from '../../Webservice/Webservice';
import MapPicker from '../../components/MapPicker';
import WAREHOUSE_ROUTES from '../../API_Route/WarehouseRoute';
import { indianStates } from '../../Utils/states';

const PAGE_SIZE = 10;
const Warehouse = () => {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [warehouseForm, setWarehouseForm] = useState({
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        latitude: '',
        longitude: '',
        contactPerson: '',
        phoneNumber: '',
        email: '',
        status: 'active'
    });
    const [formMode, setFormMode] = useState('edit');
    const isEditModeRef = useRef(false);
    const editWarehouseIdRef = useRef(null);
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Validation state
    const [errors, setErrors] = useState({});

    // Pagination handler
    const handlePageChange = (event, newPage) => {
        setMeta((prev) => ({ ...prev, page: newPage }));
    };
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [warehouseToDelete, setWarehouseToDelete] = useState(null);

    const handleDeleteClick = (warehouse, e) => {
        e.stopPropagation();
        setWarehouseToDelete(warehouse);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setWarehouseToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!warehouseToDelete) return;
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            await deleteData(`warehouse/delete/${warehouseToDelete._id || warehouseToDelete.id}`, token);
            const data = await getData('warehouse/list', null, token, false);
            setWarehouses(data?.warehouse || []);
        } catch (error) {
            console.error('Error deleting warehouse:', error);
        } finally {
            handleCloseDeleteDialog();
        }
    };

    const handleShowCreateForm = () => {
        setShowCreateForm(true);
        isEditModeRef.current = false;
        setFormMode('edit');
        setWarehouseForm({
            name: '',
            address: '',
            city: '',
            state: '',
            country: '',
            pincode: '',
            latitude: '',
            longitude: '',
            contactPerson: '',
            phoneNumber: '',
            email: '',
            status: 'active'
        });
        editWarehouseIdRef.current = null;
        setErrors({});
    };

    const handleHideCreateForm = () => {
        setShowCreateForm(false);
        isEditModeRef.current = false;
        editWarehouseIdRef.current = null;
        setErrors({});
    };

    const handleEditWarehouse = (warehouse) => {
        setShowCreateForm(true);
        isEditModeRef.current = true;
        setFormMode('view');
        editWarehouseIdRef.current = warehouse._id || warehouse.id;
        setWarehouseForm({
            name: warehouse.name || '',
            address: warehouse.address || '',
            city: warehouse.city || '',
            state: warehouse.state || '',
            country: warehouse.country || '',
            pincode: warehouse.pincode || '',
            latitude: warehouse.latitude || '',
            longitude: warehouse.longitude || '',
            contactPerson: warehouse.contactPerson || '',
            phoneNumber: warehouse.phoneNumber || '',
            email: warehouse.email || '',
            status: warehouse.status || 'active'
        });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setWarehouseForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Validation logic
    const validateForm = () => {
        const newErrors = {};

        // Name
        if (!warehouseForm.name || !warehouseForm.name.toString().trim()) {
            newErrors.name = 'Warehouse name is required';
        }

        // Contact person
        if (!warehouseForm.contactPerson || !warehouseForm.contactPerson.toString().trim()) {
            newErrors.contactPerson = 'Contact person is required';
        }

        // Phone - basic 10 digit check
        const rawPhone = (warehouseForm.phoneNumber || '').toString();
        const cleanedPhone = rawPhone.replace(/\D/g, '');
        if (!cleanedPhone) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^\d{10}$/.test(cleanedPhone)) {
            newErrors.phoneNumber = 'Enter a valid 10-digit phone number';
        }

        // Email
        if (!warehouseForm.email || !warehouseForm.email.toString().trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^\S+@\S+\.\S+$/.test(warehouseForm.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Address fields
        if (!warehouseForm.address || !warehouseForm.address.toString().trim()) {
            newErrors.address = 'Address is required';
        }
        if (!warehouseForm.city || !warehouseForm.city.toString().trim()) {
            newErrors.city = 'City is required';
        }
        if (!warehouseForm.state || !warehouseForm.state.toString().trim()) {
            newErrors.state = 'State is required';
        }
        if (!warehouseForm.country || !warehouseForm.country.toString().trim()) {
            newErrors.country = 'Country is required';
        }
        if (!warehouseForm.pincode || !warehouseForm.pincode.toString().trim()) {
            newErrors.pincode = 'Pincode is required';
        } else {
            const p = warehouseForm.pincode.toString().trim();
            if (!/^\d{4,6}$/.test(p)) {
                newErrors.pincode = 'Enter a valid pincode (4 to 6 digits)';
            }
        }

        // Latitude & Longitude (optional but if one present require both)
        const lat = warehouseForm.latitude?.toString()?.trim();
        const lng = warehouseForm.longitude?.toString()?.trim();
        if ((lat && !lng) || (!lat && lng)) {
            newErrors.latitude = 'Both latitude and longitude must be present';
            newErrors.longitude = 'Both latitude and longitude must be present';
        } else if (lat && lng) {
            if (isNaN(Number(lat)) || Number(lat) < -90 || Number(lat) > 90) {
                newErrors.latitude = 'Latitude must be a valid number between -90 and 90';
            }
            if (isNaN(Number(lng)) || Number(lng) < -180 || Number(lng) > 180) {
                newErrors.longitude = 'Longitude must be a valid number between -180 and 180';
            }
        }

        // Status
        if (!warehouseForm.status) {
            newErrors.status = 'Status is required';
        } else if (!['active', 'inactive'].includes(warehouseForm.status)) {
            newErrors.status = 'Invalid status';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Clear errors live when user corrects fields
    useEffect(() => {
        setErrors(prev => {
            const next = { ...prev };

            if (next.name && warehouseForm.name && warehouseForm.name.toString().trim()) delete next.name;
            if (next.contactPerson && warehouseForm.contactPerson && warehouseForm.contactPerson.toString().trim()) delete next.contactPerson;

            const rawPhone = (warehouseForm.phoneNumber || '').toString();
            const cleanedPhone = rawPhone.replace(/\D/g, '');
            if (next.phoneNumber && cleanedPhone && /^\d{10}$/.test(cleanedPhone)) delete next.phoneNumber;

            if (next.email && warehouseForm.email && /^\S+@\S+\.\S+$/.test(warehouseForm.email)) delete next.email;

            if (next.address && warehouseForm.address && warehouseForm.address.toString().trim()) delete next.address;
            if (next.city && warehouseForm.city && warehouseForm.city.toString().trim()) delete next.city;
            if (next.state && warehouseForm.state && warehouseForm.state.toString().trim()) delete next.state;
            if (next.country && warehouseForm.country && warehouseForm.country.toString().trim()) delete next.country;
            if (next.pincode) {
                const p = warehouseForm.pincode?.toString()?.trim() || '';
                if (p && /^\d{4,6}$/.test(p)) delete next.pincode;
            }

            // lat/lng cleanup
            const lat = warehouseForm.latitude?.toString()?.trim();
            const lng = warehouseForm.longitude?.toString()?.trim();
            if ((next.latitude || next.longitude) && lat && lng) {
                let latOk = !isNaN(Number(lat)) && Number(lat) >= -90 && Number(lat) <= 90;
                let lngOk = !isNaN(Number(lng)) && Number(lng) >= -180 && Number(lng) <= 180;
                if (latOk) delete next.latitude;
                if (lngOk) delete next.longitude;
                if (latOk && lngOk) {
                    delete next.latitude;
                    delete next.longitude;
                }
            }

            if (next.status && warehouseForm.status && ['active', 'inactive'].includes(warehouseForm.status)) delete next.status;

            return next;
        });
    }, [warehouseForm]);

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        // validate first
        if (!validateForm()) {
            // do not proceed if validation fails
            return;
        }

        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const payload = {
                name: warehouseForm.name,
                address: warehouseForm.address,
                city: warehouseForm.city,
                state: warehouseForm.state,
                country: warehouseForm.country,
                pincode: warehouseForm.pincode,
                latitude: warehouseForm.latitude,
                longitude: warehouseForm.longitude,
                contactPerson: warehouseForm.contactPerson,
                phoneNumber: warehouseForm.phoneNumber,
                email: warehouseForm.email,
                status: warehouseForm.status
            };
            if (isEditModeRef.current && editWarehouseIdRef.current) {
                await putData(`warehouse/update/${editWarehouseIdRef.current}`, payload, token);
            } else {
                await postData(WAREHOUSE_ROUTES.ADD_WAREHOUSE, payload, token, true);
            }
            handleHideCreateForm();
            const data = await getData(WAREHOUSE_ROUTES.GET_WAREHOUSE, null, token, false);
            setWarehouses(data?.warehouse || []);
        } catch (error) {
            console.error('Error submitting warehouse:', error);
        }
    };

    const handleFormModeChange = () => {
        setFormMode(prevMode => prevMode === 'view' ? 'edit' : 'view');
    };

    // Add fetchAddressFromLatLng function for reverse geocoding
    const fetchAddressFromLatLng = async (lat, lng) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
            );
            const data = await response.json();
            const address = data.address || {};
            return {
                address: data.display_name || '',
                city: address.city || address.town || address.village || address.hamlet || '',
                state: address.state || '',
                country: address.country || '',
                pincode: address.postcode || ''
            };
        } catch (error) {
            console.error('Error fetching address:', error);
            return {
                address: '',
                city: '',
                state: '',
                country: '',
                pincode: ''
            };
        }
    };

    useEffect(() => {
        const fetchWarehouses = async (page = 1) => {
            setLoading(true);
            try {
                const tokenValue = sessionStorage.getItem('token');
                const token = tokenValue ? `Bearer ${tokenValue}` : '';
                const params = {
                    page,
                    limit: PAGE_SIZE
                };
                const data = await getData('warehouse/list', params, token, false);
                setWarehouses(data?.warehouse || []);
                const meta = data?.meta || { page: 1, totalPages: 1, total: 0 };
                setMeta({
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total
                });
            } catch (error) {
                setWarehouses([]);
                setMeta({ page: 1, totalPages: 1, total: 0 });
            } finally {
                setLoading(false);
            }
        };
        fetchWarehouses(meta.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.page]);

    const renderViewField = (label, value) => (
        <Box sx={{ p: 1 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography>{value || '-'}</Typography>
        </Box>
    );

    return (
        <Box p={3}>
            {!showCreateForm ? (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ gap: 2 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h3" sx={{ fontFamily: 'Mulish, sans-serif', color: '#1a202c' }}>Warehouse List</Typography>
                            <Chip
                                label={`${meta.total || warehouses.length} ${meta.total === 1 ? 'Item' : 'Items'}`}
                                size="small"
                                sx={{ 
                                    backgroundColor: 'rgba(255, 209, 2, 0.2)', 
                                    color: '#b39200',
                                    fontFamily: 'Mulish, sans-serif', 
                                    fontWeight: '800',
                                    border: '1px solid rgba(255, 209, 2, 0.5)'
                                }}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<FaWarehouse />}
                            onClick={handleShowCreateForm}
                            sx={{
                                fontFamily: 'Mulish, sans-serif',
                                background: 'rgb(255 209 2)',
                                color: 'black',
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                px: 3,
                                boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                                border: '1px solid black',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: 'rgb(255 209 2)',
                                    boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            Create Warehouse
                        </Button>
                    </Box>

                    <TableContainer component={Paper} elevation={0} sx={{
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        border:'1px solid #d3d3d3de',
                                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={{ 
                                    '& th': {
                                        backgroundColor: '#f8f9fa',
                                        fontWeight: 800,
                                        py: 2,
                                        fontSize: '0.75rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#5c636a',
                                        fontFamily: 'Mulish, sans-serif',
                                        borderBottom: '2px solid #edeff1',
                                    }
                                }}>
                                    <TableCell>Warehouse Details</TableCell>
                                    <TableCell>Contact Person</TableCell>
                                    <TableCell>Contact Info</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton width={200} /></TableCell>
                                            <TableCell><Skeleton width={140} /></TableCell>
                                            <TableCell><Skeleton width={100} /></TableCell>
                                            <TableCell><Skeleton width={60} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : warehouses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                <img
                                                    style={{ width: '180px' }}
                                                    src='./img/nodata.png' alt='nodata' />
                                                <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif', color: 'text.secondary' }}>
                                                    No warehouses found.
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    warehouses.map((row, idx) => (
                                        <TableRow
                                            key={idx}
                                            hover
                                            onClick={() => handleEditWarehouse(row)}
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#f9fafb' },
                                                transition: 'background-color 0.2s ease'
                                            }}
                                        >
                                            <TableCell>
                                                <Typography 
                                                    fontWeight={700} 
                                                    sx={{ 
                                                        fontFamily: 'Mulish, sans-serif', 
                                                        fontSize: '0.95rem',
                                                        color: '#1a1c1e',
                                                        '&:hover': { color: '#0d47a1' }
                                                    }}
                                                >
                                                    {row.name}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{fontFamily:'Mulish, sans-serif', color: '#9ca3af', display: 'block'}}
                                                >
                                                    {row.city}, {row.state}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography 
                                                    fontWeight={600} 
                                                    sx={{fontFamily:'Mulish, sans-serif', color: '#374151', fontSize: '0.85rem'}}
                                                >
                                                    {row.contactPerson}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography 
                                                    fontWeight={500} 
                                                    sx={{fontFamily:'Mulish, sans-serif', color: '#4b5563', fontSize: '0.9rem'}}
                                                >
                                                    {row.phoneNumber}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{fontFamily:'Mulish, sans-serif', color: '#9ca3af', display: 'block'}}
                                                >
                                                    {row.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    sx={{ 
                                                        fontFamily:'Mulish, sans-serif',
                                                        fontWeight: 700,
                                                        fontSize: '0.65rem',
                                                        textTransform: 'uppercase',
                                                        borderRadius: '6px',
                                                        backgroundColor: row.status === 'active' ? '#def7ec' : '#f3f4f6',
                                                        color: row.status === 'active' ? '#03543f' : '#374151',
                                                        border: 'none',
                                                        '& .MuiChip-label': { px: 1 }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    sx={{ 
                                                        color: '#dc2626',
                                                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                                        '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' },
                                                        borderRadius: '8px'
                                                    }}
                                                    onClick={(e) => handleDeleteClick(row, e)}
                                                    aria-label="delete"
                                                >
                                                    <MdDeleteForever size={20} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination Component (centered, like Categories/ActivityLog) */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
                        <Pagination
                            count={meta.totalPages}
                            page={meta.page}
                            onChange={handlePageChange}
                            sx={{
                                '& .MuiPagination-ul': { flexWrap: 'nowrap' },
                                '& .MuiPaginationItem-root': {
                                    fontFamily: 'Mulish, sans-serif',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgb(255, 209, 2)',
                                        color: 'black',
                                        '&:hover': {
                                            backgroundColor: 'rgb(255, 190, 0)',
                                        }
                                    }
                                }
                            }}
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                    {meta.total > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                            Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
                        </Typography>
                    )}
                </>
            ) : (
                <>

                    <Paper sx={{ p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)',borderRadius: '12px' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                {isEditModeRef.current ? (formMode === 'edit' ? 'Edit Warehouse' : 'View Warehouse') : 'Create New Warehouse'}
                            </Typography>

                        
                            <Box display="flex" justifyContent="end" alignItems="center" sx={{ gap: 1 }}>
                                {isEditModeRef.current && (
                                <ToggleButton
                                    value={formMode}
                                    onClick={handleFormModeChange}
                                    aria-label={formMode === 'view' ? 'switch to edit mode' : 'switch to view mode'}
                                    size="small"
                                    sx={{
                                fontFamily: 'Mulish, sans-serif',
                                color: 'black',
                                background: 'rgba(0, 0, 0, 0.08)',
                                '&:hover': { background: 'rgba(0, 0, 0, 0.08)' }
                                }}
                                >
                                    {formMode === 'view' ? (
                                        <>
                                            <FaEdit style={{ marginRight: 8 }} />
                                            Edit
                                        </>
                                    ) : (
                                        <>
                                            <FaEye style={{ marginRight: 8 }} />
                                            View
                                        </>
                                    )}
                                </ToggleButton>
                            )}
                        <Button
                            variant="contained"
                            // startIcon={<FaArrowLeft />}
                            onClick={handleHideCreateForm}
                            sx={{  color: 'white', 
                                backgroundColor: 'red', 
                                fontFamily: 'Mulish, sans-serif',
                            '&:hover': {
                                        bgcolor: 'red'
                                        } }}
                        >
                            Back
                        </Button>
                    </Box>

                            
                        </Box>

                        <form onSubmit={handleSubmit} id="warehouse-form">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" fontWeight={600} mb={1} sx={{ fontFamily: 'Mulish, sans-serif' }}>Basic Information</Typography>
                                </Grid>

                                {/* Warehouse Name */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Warehouse Name', warehouseForm.name)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Warehouse Name"
                                            name="name"
                                            value={warehouseForm.name}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.name}
                                            helperText={errors.name}
                                        />
                                    )}
                                </Grid>

                                {/* Contact Person */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Contact Person', warehouseForm.contactPerson)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Contact Person"
                                            name="contactPerson"
                                            value={warehouseForm.contactPerson}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.contactPerson}
                                            helperText={errors.contactPerson}
                                        />
                                    )}
                                </Grid>

                                {/* Phone Number */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Phone Number', warehouseForm.phoneNumber)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            name="phoneNumber"
                                            value={warehouseForm.phoneNumber}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.phoneNumber}
                                            helperText={errors.phoneNumber}
                                        />
                                    )}
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Email', warehouseForm.email)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={warehouseForm.email}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.email}
                                            helperText={errors.email}
                                        />
                                    )}
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Status', warehouseForm.status)
                                    ) : (
                                        <TextField
                                            select
                                            fullWidth
                                            label="Status"
                                            name="status"
                                            value={warehouseForm.status}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.status}
                                            helperText={errors.status}
                                        >
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                        </TextField>
                                    )}
                                </Grid>

                                <Grid item xs={12} mt={2}>
                                    <Typography variant="subtitle1" fontWeight={600} mb={1} sx={{ fontFamily: 'Mulish, sans-serif' }}>Address Information</Typography>
                                </Grid>

                             


                                {/* Map Picker */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Location on Map</Typography>
                                    <MapPicker
                                        lat={warehouseForm.latitude}
                                        lng={warehouseForm.longitude}
                                        onChange={async ({ lat, lng }) => {
                                            // Fetch address and components from lat/lng
                                            const addr = await fetchAddressFromLatLng(lat, lng);
                                            setWarehouseForm(prev => ({
                                                ...prev,
                                                latitude: lat,
                                                longitude: lng,
                                                address: addr.address,
                                                city: addr.city,
                                                state: addr.state,
                                                country: addr.country,
                                                pincode: addr.pincode
                                            }));
                                        }}
                                    />
                                </Grid>

                                   {/* Address */}
                                <Grid item xs={12}>
                                    {formMode === 'view' ? (
                                        renderViewField('Address', warehouseForm.address)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Address"
                                            name="address"
                                            value={warehouseForm.address}
                                            onChange={handleChange}
                                            required
                                            multiline
                                            rows={2}
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.address}
                                            helperText={errors.address}
                                        />
                                    )}
                                </Grid>

                                {/* City */}
                                <Grid item xs={12} sm={6} md={3}>
                                    {formMode === 'view' ? (
                                        renderViewField('City', warehouseForm.city)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="City"
                                            name="city"
                                            value={warehouseForm.city}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.city}
                                            helperText={errors.city}
                                        />
                                    )}
                                </Grid>

                                 {/* State */}
                                <Grid item xs={12} sm={6} md={3}>
                                    {formMode === 'view' ? (
                                        renderViewField('State', warehouseForm.state)
                                    ) : (
                                        <TextField
                                            select
                                            fullWidth
                                            label="State"
                                            name="state"
                                            value={warehouseForm.state}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.state}
                                            helperText={errors.state}
                                        >
                                            {indianStates.map((state) => (
                                                <MenuItem key={state} value={state}>
                                                    {state}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                </Grid>

                                {/* Country */}
                                <Grid item xs={12} sm={6} md={3}>
                                    {formMode === 'view' ? (
                                        renderViewField('Country', warehouseForm.country)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Country"
                                            name="country"
                                            value={warehouseForm.country}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.country}
                                            helperText={errors.country}
                                        />
                                    )}
                                </Grid>

                                {/* Pincode */}
                                <Grid item xs={12} sm={6} md={3}>
                                    {formMode === 'view' ? (
                                        renderViewField('Pincode', warehouseForm.pincode)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Pincode"
                                            name="pincode"
                                            value={warehouseForm.pincode}
                                            onChange={handleChange}
                                            required
                                            variant="outlined"
                                            size="small"
                                            error={!!errors.pincode}
                                            helperText={errors.pincode}
                                        />
                                    )}
                                </Grid>

                                {/* Latitude */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Latitude', warehouseForm.latitude)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Latitude"
                                            name="latitude"
                                            value={warehouseForm.latitude}
                                            onChange={handleChange}
                                            variant="outlined"
                                            size="small"
                                            InputProps={{ readOnly: true }}
                                            error={!!errors.latitude}
                                            helperText={errors.latitude}
                                        />
                                    )}
                                </Grid>

                                {/* Longitude */}
                                <Grid item xs={12} sm={6} md={4}>
                                    {formMode === 'view' ? (
                                        renderViewField('Longitude', warehouseForm.longitude)
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label="Longitude"
                                            name="longitude"
                                            value={warehouseForm.longitude}
                                            onChange={handleChange}
                                            variant="outlined"
                                            size="small"
                                            InputProps={{ readOnly: true }}
                                            error={!!errors.longitude}
                                            helperText={errors.longitude}
                                        />
                                    )}
                                </Grid>

                                <Grid item xs={12} mt={4}>
                                    <Box display="flex" justifyContent="flex-end" gap={2}>
                                        <Button
                                            onClick={handleHideCreateForm}
                                            variant="outlined"
                                            sx={{
                                                fontFamily: 'Mulish, sans-serif',
                                                px: 3,
                                                py: 1,
                                                borderRadius: '4px',
                                                minWidth: '120px',
                                                color: 'red',
                                                borderColor: 'red',
                                                '&:hover': {
                                                    borderColor: 'red',
                                                    backgroundColor: 'rgba(255, 0, 0, 0.1)'
                                                }
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        {formMode === 'edit' && (
                                            <Button
                                                type="submit"
                                                form="warehouse-form"
                                                variant="contained"
                                                sx={{
                                                    fontFamily: 'Mulish, sans-serif',
                                                    bgcolor: 'rgb(255 209 2)',
                                                    color: 'black',
                                                    px: 3,
                                                    py: 1,
                                                    borderRadius: '4px',
                                                    minWidth: '180px',
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        bgcolor: 'rgb(255 209 2)'
                                                    }
                                                }}
                                            >
                                                {isEditModeRef.current ? 'Update Warehouse' : 'Create Warehouse'}
                                            </Button>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>
                </>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    style: {
                        borderRadius: '10px',
                        padding: '20px',
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}>
                    <img style={{ width: '80px' }} src='./img/del1.png' alt='delete' />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ textAlign: 'center', fontSize: '16px', mb: 1 }}>
                        {warehouseToDelete && (
                            <>Are you sure you want to delete warehouse <b>{warehouseToDelete.name}</b>?</>
                        )}
                    </DialogContentText>
                    <Box sx={{
                        textAlign: 'center',
                        backgroundColor: '#fff3cd',
                        padding: '12px',
                        borderRadius: '4px',
                        borderLeft: '4px solid #ffc107',
                        marginBottom: '2px'
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#856404', marginBottom: '3px' }}>
                            Warning
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#856404' }}>
                            By deleting, you won't be able to access it again.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: '20px', padding: '20px' }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        variant="outlined"
                        sx={{
                            minWidth: '120px',
                            borderColor: 'gray',
                            color: 'gray',
                            '&:hover': {
                                borderColor: 'darkgray',
                                backgroundColor: 'rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        No, Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            minWidth: '120px',
                            backgroundColor: 'red',
                            '&:hover': {
                                backgroundColor: '#bb2d3b'
                            }
                        }}
                        autoFocus
                    >
                        Yes, Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Warehouse;
